import axios from "axios";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Auth is cookie-based (httpOnly + withCredentials). No localStorage
// fallback — that would just re-open the XSS token-exfil surface we're
// trying to close. Cookies flow automatically on every axios request.

/**
 * Response interceptor
 * ------------------------------------------------------------------
 * Auth errors (401/403) get logged once and re-thrown with a stable
 * `__handled` flag so React's dev error overlay stops treating them as
 * "uncaught runtime errors" when a call fires from a hook that isn't
 * wrapped in try/catch (e.g. race conditions on route switches). Callers
 * that DO try/catch still get the same error object and same message.
 */
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    if (status === 401 || status === 403) {
      err.__handled = true;
      // Only log once per unique URL to keep the console clean
      const url = err.config?.url || "";
      if (typeof window !== "undefined" && window.__pp_authWarned) {
        if (!window.__pp_authWarned.has(url)) {
          window.__pp_authWarned.add(url);
          console.warn(`[api] ${status} on ${url} — user's role doesn't permit this call.`);
        }
      } else if (typeof window !== "undefined") {
        window.__pp_authWarned = new Set([url]);
        console.warn(`[api] ${status} on ${url} — user's role doesn't permit this call.`);
      }
    }
    // 429 rate-limit — surface a friendly sonner toast so users understand
    // the throttle instead of a raw axios error. The toast counts down from
    // the `Retry-After` header (seconds) so users know exactly when the
    // budget resets. Debounced per URL so retries don't stack toasts.
    if (status === 429) {
      const url = err.config?.url || "";
      const isAi = /\/(ai|admin\/ai)\b/.test(url);
      const detail = err?.response?.data?.detail;
      const retryHdr = err?.response?.headers?.["retry-after"]
        || err?.response?.headers?.["x-rate-limit-retry-after"];
      const retryAfter = Math.max(1, Math.min(60, parseInt(retryHdr, 10) || 15));
      // Strip any embedded 'Try again in Ns' from the server detail — we
      // replace it with a live-ticking value below.
      const cleanDetail = (typeof detail === "string" && detail)
        ? detail.replace(/Try again in \d+s\s*[—-]?\s*/i, "")
        : "";
      const baseMsg = cleanDetail
        || (isAi
          ? "You're going a little fast for our AI budget. It resets in a moment."
          : "Too many requests — please slow down.");
      if (typeof window !== "undefined") {
        window.__pp_429Toasts = window.__pp_429Toasts || new Map();
        const last = window.__pp_429Toasts.get(url) || 0;
        const now = Date.now();
        if (now - last > 4000) {          // one countdown per URL per 4s
          window.__pp_429Toasts.set(url, now);
          try {
            const toastId = `429:${url}`;
            const title = isAi ? "AI rate limit" : "Rate limit";
            // Initial toast with countdown starting value
            toast.warning(title, {
              description: `${baseMsg} Try again in ${retryAfter}s.`,
              duration: (retryAfter + 1) * 1000,
              id: toastId,
            });
            // Live-tick the countdown every second until it hits zero.
            let remaining = retryAfter;
            const iv = setInterval(() => {
              remaining -= 1;
              if (remaining <= 0) {
                clearInterval(iv);
                toast.success("Ready — you can try again", {
                  id: toastId,
                  duration: 2500,
                });
                return;
              }
              toast.warning(title, {
                description: `${baseMsg} Try again in ${remaining}s.`,
                duration: (remaining + 1) * 1000,
                id: toastId,
              });
            }, 1000);
          } catch (err) { if (typeof console !== 'undefined') console.debug('lib/api.js: swallowed', err); }
        }
      }
      err.__handled = true;
    }
    return Promise.reject(err);
  },
);

/**
 * Global "unhandledrejection" guard.
 * ------------------------------------------------------------------
 * React's dev-only ErrorOverlay listens on `unhandledrejection`. Any axios
 * 401/403 that leaks past a caller (missing try/catch, race condition on
 * an unmounting page) shows as a giant red "Uncaught runtime errors"
 * dialog — extremely noisy and demo-breaking. This listener intercepts
 * only auth errors flagged with __handled by our response interceptor
 * above and marks them handled, so the overlay skips them. Actual bugs
 * (network fail, 5xx, JS errors) still surface normally.
 */
if (typeof window !== "undefined" && !window.__pp_unhandled_hooked) {
  window.__pp_unhandled_hooked = true;
  window.addEventListener("unhandledrejection", (ev) => {
    const err = ev?.reason;
    if (err && err.__handled) {
      ev.preventDefault(); // suppress the overlay for this specific rejection
    }
  });
}

export function apiErrorMessage(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (typeof d === "object" && d.msg) return d.msg;
  return String(d);
}
